import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { QuestionImage } from "@/components/app/EvalLive";
import { toast } from "sonner";
import { ClipboardCheck, Clock, Loader2, Send, ArrowLeft, CheckCircle2 } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function TakeExam({ token, exam, onDone }) {
  const [data, setData] = useState(null);
  const [ans, setAns] = useState({});
  const [left, setLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const sent = useRef(false);
  const h = { headers: { "X-Parent-Token": token } };
  useEffect(() => { api.post(`/student/evals/${exam.id}/start`, {}, h).then(({ data }) => setData(data)).catch((e) => { toast.error(e?.response?.data?.detail || "No disponible"); onDone(); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (auto) => {
    if (sent.current) return;
    if (!auto && !window.confirm("¿Entregar el examen? Ya no podrás cambiar respuestas.")) return;
    sent.current = true; setBusy(true);
    try { const { data: r } = await api.post(`/student/evals/${exam.id}/submit`, { answers: ans }, h); toast.success(`Examen entregado. Calificación: ${r.score}`); onDone(); }
    catch (e) { sent.current = false; toast.error(e?.response?.data?.detail || "No se pudo entregar"); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (!data?.deadline) return;
    const skew = Date.now() - new Date(data.server_now).getTime();
    const t = setInterval(() => { const s = Math.floor((new Date(data.deadline).getTime() - (Date.now() - skew)) / 1000); setLeft(s); if (s <= 0) { clearInterval(t); submit(true); } }, 1000);
    return () => clearInterval(t);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />;
  const set = (id, v) => setAns((a) => ({ ...a, [id]: v }));
  return (
    <div className="space-y-4" data-testid="student-exam">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display font-bold text-xl text-navy flex-1 break-words">{data.exam.title}</h2>
        {left !== null && <span className={`inline-flex items-center gap-1 text-sm font-bold px-3 py-1 rounded-full ${left < 120 ? "bg-red-100 text-red-700" : "bg-indigo-100 text-indigo-700"}`} data-testid="student-exam-timer"><Clock className="w-4 h-4" /> {Math.max(0, Math.floor(left / 60))}:{String(Math.max(0, left % 60)).padStart(2, "0")}</span>}
      </div>
      {data.exam.instructions && <p className="text-sm text-nardo-600 whitespace-pre-wrap">{data.exam.instructions}</p>}
      {data.exam.questions.map((q, i) => (
        <div key={q.id} className="bg-white rounded-2xl border border-nardo-200 p-4 space-y-2" data-testid={`student-q-${i}`}>
          <div className="font-semibold text-navy break-words">{i + 1}. {q.text} <span className="text-[11px] text-nardo-400 font-normal">({q.points} pts)</span></div>
          <QuestionImage tok={q.image} />
          {q.type === "mc" && q.options.map((o, j) => (
            <label key={j} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer break-words ${ans[q.id] === j ? "border-indigo-500 bg-indigo-50" : "border-nardo-200"}`}>
              <input type="radio" name={q.id} checked={ans[q.id] === j} onChange={() => set(q.id, j)} className="mt-1 accent-indigo-600" data-testid={`student-q-${i}-opt-${j}`} /> <span>{o}</span>
            </label>
          ))}
          {q.type === "tf" && <div className="flex gap-2">{[[true, "Verdadero"], [false, "Falso"]].map(([v, l]) => <button key={l} onClick={() => set(q.id, v)} data-testid={`student-q-${i}-${v}`} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${ans[q.id] === v ? "bg-indigo-600 text-white border-indigo-600" : "border-nardo-200 text-navy"}`}>{l}</button>)}</div>}
          {q.type === "match" && q.left.map((l, k) => (
            <div key={k} className="grid sm:grid-cols-2 gap-2 items-center">
              <div className="text-sm text-navy break-words">{l}</div>
              <select className="border border-nardo-200 rounded-lg px-3 py-2 text-base sm:text-sm" value={(ans[q.id] || {})[k] || ""} onChange={(e) => set(q.id, { ...(ans[q.id] || {}), [k]: e.target.value })} data-testid={`student-q-${i}-match-${k}`}>
                <option value="">Elige…</option>{q.right.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          ))}
        </div>
      ))}
      <button onClick={() => submit(false)} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="student-exam-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Entregar examen</button>
    </div>
  );
}

export default function StudentEvals({ token }) {
  const [items, setItems] = useState(null);
  const [taking, setTaking] = useState(null);
  const load = () => api.get("/student/evals", { headers: { "X-Parent-Token": token } }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  if (taking) return <div><button onClick={() => setTaking(null)} className="text-xs text-indigo-700 mb-3 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Volver</button><TakeExam token={token} exam={taking} onDone={() => { setTaking(null); load(); }} /></div>;
  if (!items) return <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />;
  if (!items.length) return <div className="text-center text-nardo-500 py-10" data-testid="student-evals-empty">No tienes exámenes por ahora.</div>;
  return (
    <div className="space-y-2" data-testid="student-evals">
      {items.map((x) => (
        <div key={x.id} className="bg-white rounded-2xl border border-nardo-200 p-4 flex flex-wrap items-center gap-3" data-testid={`student-eval-${x.id}`}>
          <ClipboardCheck className="w-5 h-5 text-indigo-600 shrink-0" />
          <div className="flex-1 min-w-[180px]">
            <div className="font-semibold text-navy break-words">{x.title}</div>
            <div className="text-[11px] text-nardo-500">{x.subject} · {x.n_questions} preguntas{x.time_limit_min ? ` · ${x.time_limit_min} min` : ""}{x.mode === "horario" ? ` · ${fmt(x.start_at)} a ${fmt(x.end_at)}` : ""}</div>
          </div>
          {x.submitted ? <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700"><CheckCircle2 className="w-4 h-4" /> {x.score}</span>
            : x.available ? <button onClick={() => setTaking(x)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold" data-testid={`student-eval-start-${x.id}`}>{x.started ? "Continuar" : "Comenzar"}</button>
            : <span className="text-xs text-nardo-400">No disponible</span>}
        </div>
      ))}
    </div>
  );
}
